/**
 * Goods Receipts Filters Component
 *
 * Filters for the goods receipts list.
 * @see US-9.4 - Page Liste Receptions
 */

import { Select } from '@/components/ui';
import { DatePicker, type DateRange } from '@/components/ui';
import { useActiveSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { GoodsReceiptStatus, GOODS_RECEIPT_STATUS_LABELS } from '@bstock/shared';

interface GoodsReceiptsFiltersProps {
  supplierId: string;
  status: string;
  dateRange: DateRange;
  onSupplierChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onDateRangeChange: (range: DateRange) => void;
  /** Si false, le filtre fournisseur est masqué (évite 403 pour OPERATOR) */
  showSupplierFilter?: boolean;
}

const statusOptions = [
  { value: '', label: 'Tous les statuts' },
  { value: GoodsReceiptStatus.DRAFT, label: GOODS_RECEIPT_STATUS_LABELS[GoodsReceiptStatus.DRAFT] },
  { value: GoodsReceiptStatus.VALIDATED, label: GOODS_RECEIPT_STATUS_LABELS[GoodsReceiptStatus.VALIDATED] },
  { value: GoodsReceiptStatus.CANCELLED, label: GOODS_RECEIPT_STATUS_LABELS[GoodsReceiptStatus.CANCELLED] },
];

export function GoodsReceiptsFilters({
  supplierId,
  status,
  dateRange,
  onSupplierChange,
  onStatusChange,
  onDateRangeChange,
  showSupplierFilter = true,
}: GoodsReceiptsFiltersProps) {
  // Ne charger les fournisseurs que si le filtre est affiché (évite 403)
  const { data: suppliers, isLoading: suppliersLoading } = useActiveSuppliers({
    enabled: showSupplierFilter,
  });

  const supplierOptions = [
    { value: '', label: 'Tous les fournisseurs' },
    ...(suppliers?.map((s) => ({ value: s.id, label: s.name })) ?? []),
  ];

  return (
    <div className="flex flex-wrap items-end gap-4">
      {/* Date Range - First */}
      <div className="w-72">
        <label className="block text-sm font-medium text-dark-500 mb-1.5">
          Periode
        </label>
        <DatePicker
          value={dateRange}
          onChange={onDateRangeChange}
          placeholder="Selectionner une periode"
        />
      </div>

      {/* Supplier - MANAGER+ uniquement */}
      {showSupplierFilter && (
        <div className="w-52">
          <Select
            label="Fournisseur"
            value={supplierId}
            onChange={onSupplierChange}
            options={supplierOptions}
            disabled={suppliersLoading}
          />
        </div>
      )}

      {/* Status */}
      <div className="w-44">
        <Select
          label="Statut"
          value={status}
          onChange={onStatusChange}
          options={statusOptions}
        />
      </div>
    </div>
  );
}
